import { PaloListFilterField, PaloListFilterOperator, paloDefaultOperator } from './use-palo-list-filters';

/** A comparison as the address carries it: one value, or two for a range. */
export interface PaloComparison {
    operator: PaloListFilterOperator;
    values: string[];
}

/**
 * The signs a comparison may be written with, longest first so that `>=` is
 * read before the `>` it starts with.
 *
 * Each sign now means what it says. There were only bounds that take their
 * own value with them at first, so `>3` was read as `≥3` and answered with
 * the threes as well — right by the letter of what the list could do, wrong
 * by what was asked (#2231 R-126). A field that cannot be asked for the one
 * without the other refuses the entry rather than widening it.
 */
const SIGNS: [string, PaloListFilterOperator][] = [
    ['>=', 'from'],
    ['=>', 'from'],
    ['≥', 'from'],
    ['>', 'gt'],
    ['<=', 'until'],
    ['=<', 'until'],
    ['≤', 'until'],
    ['<', 'lt'],
    ['=', 'eq'],
];

/** `3-5`, `3 – 5`, `3..5`, `3 bis 5` — all one range. */
const RANGE = /^(.+?)\s*(?:\.\.|–|—|-|bis)\s*(.+)$/i;

/**
 * A typed number, read the way it is written here.
 *
 * The screens are German, so the comma is the decimal separator and the dot
 * groups the thousands — but only where it really groups them (`1.000`), not
 * where it separates a fraction someone typed in the English way (`1000.50`).
 */
const toNumber = (text: string): string | null => {
    const trimmed = text.replace(/[\s']/g, '');
    const grouped = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(trimmed);
    const plain = grouped ? trimmed.replace(/\./g, '') : trimmed;
    const normalised = plain.replace(',', '.');

    return /^-?\d+(\.\d+)?$/.test(normalised) ? normalised : null;
};

const signOf = (text: string): [PaloListFilterOperator, string] | null => {
    const sign = SIGNS.find(([written]) => text.startsWith(written));

    return sign ? [sign[1], text.substring(sign[0].length).trim()] : null;
};

/**
 * What someone typed into a number box, as a comparison the list can answer.
 *
 * `>3` and `1000-2000` were sent as the value of an equals until now, which
 * the list answered with a server error (#2231 R-117). Anything that is not a
 * comparison this field offers comes back as `null`, and an unanswerable
 * filter is then marked in the box instead of being sent.
 */
export const paloParseComparison = (text: string, field: PaloListFilterField): PaloComparison | null => {
    const trimmed = text.trim();

    if ('' === trimmed) {
        return null;
    }

    const offers = (operator: PaloListFilterOperator): boolean => field.operators.includes(operator);
    const range = RANGE.exec(trimmed);

    if (range && offers('between')) {
        const from = toNumber(range[1]);
        const until = toNumber(range[2]);

        return from && until ? { operator: 'between', values: [from, until] } : null;
    }

    const signed = signOf(trimmed);
    const operator = signed ? signed[0] : paloDefaultOperator(field);
    const value = toNumber(signed ? signed[1] : trimmed);

    return value && offers(operator) ? { operator, values: [value] } : null;
};

/**
 * The same number, written the way the screens write numbers. Only an amount
 * groups its thousands: a document number is a name, and 154561 read as
 * "154 561" in the box and "154.561" in the chip beside it.
 */
const writtenAs = (value: string, grouped: boolean): string =>
    Number.isFinite(Number(value))
        ? new Intl.NumberFormat('de-AT', { maximumFractionDigits: 10, useGrouping: grouped }).format(Number(value))
        : value;

/**
 * A comparison as the box shows it.
 *
 * Typing `>=3` leaves `≥ 3` standing, so what the list was asked is readable
 * where it was asked — and reads back as the same comparison.
 */
export const paloWriteComparison = (comparison: PaloComparison, isAmount = false): string => {
    const written = (value: string): string => writtenAs(value, isAmount);

    switch (comparison.operator) {
        case 'from':
            return `≥ ${written(comparison.values[0] ?? '')}`;
        case 'until':
            return `≤ ${written(comparison.values[0] ?? '')}`;
        case 'gt':
            return `> ${written(comparison.values[0] ?? '')}`;
        case 'lt':
            return `< ${written(comparison.values[0] ?? '')}`;
        case 'between':
            return `${written(comparison.values[0] ?? '')} – ${written(comparison.values[1] ?? '')}`;
        default:
            return written(comparison.values[0] ?? '');
    }
};
